import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Node, Edge, XYPosition } from 'reactflow'; // Import XYPosition

// Define a more specific type for node data if possible, otherwise use Record<string, any>
// Example: Assuming nodes represent Organization units or Roles
interface OrgNodeData {
  label: string;
  type: string; // e.g., 'department', 'role'
  // Add other relevant properties based on your application needs
  [key: string]: any; // Allow other properties
}

// Use the specific data type in Node generic
export type OrgNode = Node<OrgNodeData>;

interface OrganizationState {
  nodes: OrgNode[]; // Use OrgNode type
  edges: Edge[];
  selectedNode: OrgNode | null; // Use OrgNode type
  organizationType: string | null;
  isLoading: boolean;
  error: string | null;
  isDirty: boolean;
  lastSaved: string | null;
}

const initialState: OrganizationState = {
  nodes: [],
  edges: [],
  selectedNode: null,
  organizationType: null,
  isLoading: false,
  error: null,
  isDirty: false,
  lastSaved: null,
};

const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    fetchOrganizationStart: state => {
      state.isLoading = true;
      state.error = null;
    },
    // Ensure payload matches the state types
    fetchOrganizationSuccess: (
      state,
      action: PayloadAction<{
        nodes: OrgNode[];
        edges: Edge[];
        organizationType: string | null;
      }>
    ) => {
      state.nodes = action.payload.nodes;
      state.edges = action.payload.edges;
      state.organizationType = action.payload.organizationType;
      state.isLoading = false;
      state.error = null;
      state.isDirty = false;
    },
    fetchOrganizationFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    // Ensure payload matches the state types
    updateNodes: (state, action: PayloadAction<OrgNode[]>) => {
      state.nodes = action.payload;
      state.isDirty = true;
    },
    updateEdges: (state, action: PayloadAction<Edge[]>) => {
      state.edges = action.payload;
      state.isDirty = true;
    },
    // Ensure payload matches the state types
    selectNode: (state, action: PayloadAction<OrgNode | null>) => {
      state.selectedNode = action.payload;
    },
    // Use specific type for data payload
    updateNodeData: (state, action: PayloadAction<{ id: string; data: Partial<OrgNodeData> }>) => {
      const nodeIndex = state.nodes.findIndex(node => node.id === action.payload.id);
      if (nodeIndex !== -1) {
        // Create a new node object to ensure immutability if needed by React Flow or Redux Toolkit
        state.nodes[nodeIndex] = {
          ...state.nodes[nodeIndex],
          data: {
            ...state.nodes[nodeIndex].data,
            ...action.payload.data,
          },
        };
        state.isDirty = true;
      }
    },
    // Ensure payload matches the state types
    addNode: (state, action: PayloadAction<OrgNode>) => {
      state.nodes.push(action.payload);
      state.isDirty = true;
    },
    removeNode: (state, action: PayloadAction<string>) => {
      state.nodes = state.nodes.filter(node => node.id !== action.payload);
      state.edges = state.edges.filter(
        edge => edge.source !== action.payload && edge.target !== action.payload
      );
      if (state.selectedNode?.id === action.payload) {
        state.selectedNode = null; // Deselect if the removed node was selected
      }
      state.isDirty = true;
    },
    addEdge: (state, action: PayloadAction<Edge>) => {
      // Prevent adding duplicate edges if necessary
      if (!state.edges.some(edge => edge.id === action.payload.id)) {
        state.edges.push(action.payload);
        state.isDirty = true;
      }
    },
    removeEdge: (state, action: PayloadAction<string>) => {
      state.edges = state.edges.filter(edge => edge.id !== action.payload);
      state.isDirty = true;
    },
    setOrganizationType: (state, action: PayloadAction<string | null>) => {
      // Allow null
      state.organizationType = action.payload;
      state.isDirty = true;
    },
    saveOrganizationStart: state => {
      state.isLoading = true;
      state.error = null;
    },
    saveOrganizationSuccess: state => {
      state.isLoading = false;
      state.isDirty = false;
      state.lastSaved = new Date().toISOString();
    },
    saveOrganizationFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    resetOrganization: () => {
      // Return initialState directly to reset
      return initialState;
    },
  },
});

export const {
  fetchOrganizationStart,
  fetchOrganizationSuccess,
  fetchOrganizationFailure,
  updateNodes,
  updateEdges,
  selectNode,
  updateNodeData,
  addNode,
  removeNode,
  addEdge,
  removeEdge,
  setOrganizationType,
  saveOrganizationStart,
  saveOrganizationSuccess,
  saveOrganizationFailure,
  resetOrganization,
} = organizationSlice.actions;

export default organizationSlice.reducer;
